import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "../../App";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Receipt, Loader2, Clock, CheckCircle2, AlertCircle,
  Send, DollarSign, Calendar, Users, UserPlus, Plus,
  Trash2, Bell, RefreshCw, ChevronRight, Search, X
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

/**
 * ProgramInvoicesTab — org admin can:
 * 1. Create individual invoices for households in this program
 * 2. View all org-created invoices for this program
 * 3. Send reminders for pending invoices
 */
const invoiceDate = (value) => value && !Number.isNaN(new Date(value).getTime())
  ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

const ProgramInvoicesTab = ({ program, token }) => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [filter, setFilter] = useState("all");
  const [createDialog, setCreateDialog] = useState(false);
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Invoice form
  const [invoiceForm, setInvoiceForm] = useState({
    description: "",
    amount: "",
    due_days: 30,
    notes: "",
    player_ids: [],
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [invoicesRes, householdsRes] = await Promise.all([
        axios.get(`${API}/org-invoices?program_id=${program.program_id}`, { headers }),
        axios.get(`${API}/org-invoices/program-households/${program.program_id}`, { headers }),
      ]);
      setInvoices(invoicesRes.data?.items || []);
      setHouseholds(householdsRes.data || []);
    } catch (error) {
      console.error("Error fetching invoice data:", error);
    } finally {
      setLoading(false);
    }
  }, [program.program_id, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async () => {
    if (!selectedHousehold || !invoiceForm.description || !invoiceForm.amount) {
      toast.error("Please fill in all required fields");
      return;
    }

    setCreating(true);
    try {
      const amount = parseFloat(invoiceForm.amount);
      const dueDate = new Date(Date.now() + invoiceForm.due_days * 86400000).toISOString();

      await axios.post(`${API}/org-invoices`, {
        household_id: selectedHousehold.household_id,
        user_id: selectedHousehold.user_id,
        program_id: program.program_id,
        player_ids: invoiceForm.player_ids.length > 0 ? invoiceForm.player_ids : selectedHousehold.player_ids,
        description: invoiceForm.description,
        line_items: [{
          description: invoiceForm.description,
          quantity: 1,
          unit_price: amount,
          total: amount,
        }],
        due_date: dueDate,
        notes: invoiceForm.notes || null,
      }, { headers });

      toast.success(`Invoice sent to ${selectedHousehold.parent_email}`);
      setCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    } finally {
      setCreating(false);
    }
  };

  const handleRemind = async (invoiceId) => {
    try {
      await axios.post(`${API}/org-invoices/${invoiceId}/remind`, {}, { headers });
      toast.success("Reminder sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    }
  };

  const resetForm = () => {
    setSelectedHousehold(null);
    setInvoiceForm({ description: "", amount: "", due_days: 30, notes: "", player_ids: [] });
    setSearchQuery("");
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filter === "all") return true;
    return inv.status === filter;
  });

  const totalPending = invoices.filter(i => i.status === "pending").reduce((s, i) => s + (i.total || 0), 0);
  const totalPaid = invoices.filter(i => i.status === "paid").reduce((s, i) => s + (i.total || 0), 0);

  const filteredHouseholds = households.filter(h => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      h.parent_name?.toLowerCase().includes(q) ||
      h.parent_email?.toLowerCase().includes(q) ||
      h.player_names?.some(n => n.toLowerCase().includes(q))
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "paid": return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "pending": return "bg-amber-50 text-amber-700 border-amber-200";
      case "overdue": return "bg-red-50 text-red-700 border-red-200";
      default: return "bg-slate-50 text-slate-700 border-slate-200";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="program-section program-payments" data-program-section="payments">
      {/* Header */}
      <div className="program-section-heading">
        <div>
          <h2>Payments</h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage additional invoices and payment reminders for this program.
          </p>
        </div>
        <Button
          onClick={() => setCreateDialog(true)}
          className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Invoice
        </Button>
      </div>

      {/* Summary */}
      <div className="program-section-metrics">
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">${totalPending.toFixed(2)}</p>
              <p className="text-xs text-slate-500">Outstanding</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">${totalPaid.toFixed(2)}</p>
              <p className="text-xs text-slate-500">Collected</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
              <Receipt className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{invoices.length}</p>
              <p className="text-xs text-slate-500">Total Sent</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="program-filter-bar">
        {[
          { value: "all", label: "All" },
          { value: "pending", label: "Pending" },
          { value: "paid", label: "Paid" },
          { value: "overdue", label: "Overdue" },
        ].map((tab) => (
          <button
            key={tab.value}
            aria-pressed={filter === tab.value}
            onClick={() => setFilter(tab.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === tab.value
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {tab.label}
            {tab.value === "pending" && invoices.filter(i => i.status === "pending").length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
                {invoices.filter(i => i.status === "pending").length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Invoice List */}
      {filteredInvoices.length === 0 ? (
        <div className="program-empty-state">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Receipt className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="font-semibold text-slate-900 mb-2">No Invoices Yet</h3>
          <p className="text-slate-500 text-sm">
            Create invoices for uniform fees, late charges, or custom billing.
          </p>
        </div>
      ) : (
        <div className="program-data-panel program-invoice-list">
          <div className="divide-y divide-slate-100">
            {filteredInvoices.map((inv) => (
              <div
                key={inv.invoice_id}
                className="program-invoice-row"
              >
                <button type="button" className="program-invoice-summary" aria-label={`Invoice ${inv.invoice_number || inv.invoice_id} details`} aria-expanded={expandedId === inv.invoice_id}
                  onClick={() => setExpandedId(expandedId === inv.invoice_id ? null : inv.invoice_id)}>
                  <span><strong>#{inv.invoice_number || '—'}</strong><span className="program-cell-detail">{inv.description || 'No description'}</span></span>
                  <span><span>{inv.parent_name || inv.household_name || 'Household unavailable'}</span><span className="program-cell-detail">{inv.parent_email || 'Email unavailable'}</span></span>
                  <span className="program-invoice-dates"><span>Created: {invoiceDate(inv.created_at)}</span><span>Due: {invoiceDate(inv.due_date)}</span><span>Paid: {invoiceDate(inv.paid_at)}</span></span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full border ${getStatusColor(inv.status)}`}>{inv.status || 'Unknown status'}</span>
                  <strong className="program-money">{inv.total != null ? `$${inv.total.toFixed(2)}` : '—'}</strong>
                  <ChevronRight aria-hidden="true" className={`w-5 h-5 text-slate-400 ${expandedId === inv.invoice_id ? 'rotate-90' : ''}`} />
                </button>

                {expandedId === inv.invoice_id && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="bg-slate-50 rounded-xl p-4 mb-3">
                      {inv.line_items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-slate-600">{item.description}</span>
                          <span className="font-medium">${item.total?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    {inv.parent_email && (
                      <p className="text-xs text-slate-400 mb-3">Sent to: {inv.parent_email}</p>
                    )}
                    {inv.notes && <p className="text-sm text-slate-500 mb-3">{inv.notes}</p>}
                    {inv.status === "pending" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl"
                        onClick={(e) => { e.stopPropagation(); handleRemind(inv.invoice_id); }}
                      >
                        <Bell className="w-4 h-4 mr-2" />
                        Send Reminder
                      </Button>
                    )}
                    {inv.status === "paid" && inv.paid_at && (
                      <p className="text-sm text-emerald-600 flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" />
                        Paid {new Date(inv.paid_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Invoice Dialog */}
      <Dialog open={createDialog} onOpenChange={(open) => { if (!open) resetForm(); setCreateDialog(open); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogDescription>
              Send an invoice to a family in {program.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Step 1: Select household */}
            {!selectedHousehold ? (
              <div className="space-y-3">
                <label className="text-sm font-medium text-slate-700">Select Family</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {filteredHouseholds.map((hh) => (
                    <div
                      key={hh.household_id}
                      onClick={() => {
                        setSelectedHousehold(hh);
                        setInvoiceForm(prev => ({ ...prev, player_ids: hh.player_ids }));
                      }}
                      className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:border-slate-400 transition-colors"
                    >
                      <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{hh.parent_name}</p>
                        <p className="text-xs text-slate-500 truncate">{hh.parent_email}</p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {hh.player_names?.join(", ")}
                        </p>
                      </div>
                    </div>
                  ))}
                  {filteredHouseholds.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-4">No families found</p>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Selected household chip */}
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                  <Users className="w-5 h-5 text-blue-600" />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{selectedHousehold.parent_name}</p>
                    <p className="text-xs text-slate-500">{selectedHousehold.parent_email}</p>
                  </div>
                  <button onClick={() => setSelectedHousehold(null)} className="text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Description */}
                <div>
                  <label className="text-sm font-medium text-slate-700">Description *</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                    placeholder="e.g. Uniform fee, Late payment, Equipment rental"
                    value={invoiceForm.description}
                    onChange={(e) => setInvoiceForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                {/* Amount */}
                <div>
                  <label className="text-sm font-medium text-slate-700">Amount ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                    placeholder="0.00"
                    value={invoiceForm.amount}
                    onChange={(e) => setInvoiceForm(prev => ({ ...prev, amount: e.target.value }))}
                  />
                </div>

                {/* Due in days */}
                <div>
                  <label className="text-sm font-medium text-slate-700">Due In</label>
                  <select
                    className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                    value={invoiceForm.due_days}
                    onChange={(e) => setInvoiceForm(prev => ({ ...prev, due_days: parseInt(e.target.value) }))}
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={60}>60 days</option>
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-sm font-medium text-slate-700">Notes (optional)</label>
                  <textarea
                    className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                    rows={2}
                    placeholder="Any additional notes..."
                    value={invoiceForm.notes}
                    onChange={(e) => setInvoiceForm(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
              </>
            )}
          </div>

          {selectedHousehold && (
            <DialogFooter>
              <Button variant="outline" onClick={() => { resetForm(); setCreateDialog(false); }} className="rounded-xl">
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={creating || !invoiceForm.description || !invoiceForm.amount}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
              >
                {creating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Send Invoice</>
                )}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramInvoicesTab;
